import { useState, useEffect, CSSProperties } from 'react';
import { ServiceType, ProviderId, TimeSlot, ContactDetails, BookingResponse, LeadSource } from '../types';
import { createBooking } from '../utils/api';
import { detectUserTimezone } from '../utils/timezone';
import Step1ServiceSelection from '../components/steps/Step1ServiceSelection';
import Step2ProviderSelection from '../components/steps/Step2ProviderSelection';
import Step3TimeSlot from '../components/steps/Step3TimeSlot';
import Step4DetailsForm from '../components/steps/Step4DetailsForm';
import Step5Confirmation from '../components/steps/Step5Confirmation';
import ProgressBar from '../components/ProgressBar';
import Button from '../components/Button';
import Loader from '../components/Loader';
import Modal from '../components/Modal';

const VALID_SERVICES: ServiceType[] = ['demo', 'support', 'kickoff'];
const VALID_PROVIDERS: ProviderId[] = ['samuel', 'vinil', 'shawn', 'ramon', 'auto'];

function isProviderAllowedForService(service: ServiceType, provider: ProviderId) {
  if (provider === 'auto') {
    return true;
  }

  if (service === 'demo' && provider === 'vinil') {
    return false;
  }

  // Shawn and Ramon only take training meetings
  if (service !== 'support' && (provider === 'shawn' || provider === 'ramon')) {
    return false;
  }

  return true;
}

export default function BookingPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedService, setSelectedService] = useState<ServiceType | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<ProviderId | null>('auto');
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [selectedTimezone, setSelectedTimezone] = useState(detectUserTimezone());
  const [contactDetails, setContactDetails] = useState<ContactDetails>({
    firstName: '',
    lastName: '',
    email: '',
    agency: '',
    state: '',
    sellsBenefitsSeniorLife: true,
    referredBy: 'none' as LeadSource,
    referredByOther: ''
  });
  const [booking, setBooking] = useState<BookingResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize from URL parameters for deep links
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const serviceParam = params.get('service');
    const providerParam = params.get('provider');

    let initialService: ServiceType | null = null;
    let initialProvider: ProviderId | null = 'auto';

    if (serviceParam && VALID_SERVICES.includes(serviceParam as ServiceType)) {
      initialService = serviceParam as ServiceType;
    }

    if (providerParam && VALID_PROVIDERS.includes(providerParam as ProviderId)) {
      initialProvider = providerParam as ProviderId;
    }

    if (initialService && initialProvider && !isProviderAllowedForService(initialService, initialProvider)) {
      initialProvider = 'auto';
    }

    if (initialService) {
      setSelectedService(initialService);
      setCurrentStep(3);
    }

    setSelectedProvider(initialProvider);
  }, []);

  // Update URL to reflect current selections for sharable deep links
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);

    if (selectedService) {
      params.set('service', selectedService);

      if (selectedProvider) {
        params.set('provider', selectedProvider);
      } else {
        params.delete('provider');
      }
    } else {
      params.delete('service');
      params.delete('provider');
    }

    const search = params.toString();
    const newUrl = `${window.location.pathname}${search ? `?${search}` : ''}`;

    if (newUrl !== window.location.pathname + window.location.search) {
      window.history.replaceState({}, '', newUrl);
    }
  }, [selectedService, selectedProvider]);

  const handleServiceSelect = (service: ServiceType) => {
    setSelectedService(service);
    setSelectedProvider('auto');
    setSelectedSlot(null);
    setCurrentStep(2);
  };

  const handleProviderSelect = (provider: ProviderId) => {
    setSelectedProvider(provider);
    setSelectedSlot(null);
  };

  const handleSlotSelect = (slot: TimeSlot) => {
    setSelectedSlot(slot);
  };

  const handleNext = () => {
    if (currentStep === 1 && selectedService) {
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2 && selectedProvider) {
      setCurrentStep(3);
      return;
    }

    if (currentStep === 3 && selectedSlot) {
      setCurrentStep(4);
    }
  };

  const handleBack = () => {
    if (currentStep > 1 && currentStep <= 4) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!selectedService || !selectedProvider || !selectedSlot) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await createBooking({
        serviceId: selectedService,
        providerId: selectedProvider,
        slot: selectedSlot,
        tz: selectedTimezone,
        contact: contactDetails
      });

      setBooking(response);
      setCurrentStep(5);
    } catch (err: any) {
      setError(err.message || 'Failed to create booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contentStyles: CSSProperties = {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto'
  };

  const navigationStyles: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 'var(--spacing-md)',
    marginTop: 'var(--spacing-2xl)',
    maxWidth: '900px',
    margin: 'var(--spacing-2xl) auto 0'
  };

  const earlyStepNavStyles: CSSProperties = {
    ...navigationStyles,
    justifyContent: currentStep > 1 ? 'space-between' : 'flex-end'
  };

  const canProceed =
    (currentStep === 1 && !!selectedService) ||
    (currentStep === 2 && !!selectedProvider) ||
    (currentStep === 3 && !!selectedSlot);

  return (
    <div style={contentStyles}>
      {/* Progress Bar - Only show in steps 1-4 */}
      {currentStep < 5 && <ProgressBar currentStep={currentStep} totalSteps={4} />}

      {/* Step Content */}
      {currentStep === 1 && (
        <Step1ServiceSelection
          selectedService={selectedService}
          onSelectService={handleServiceSelect}
        />
      )}

      {currentStep === 2 && selectedService && (
        <Step2ProviderSelection
          selectedService={selectedService}
          selectedProvider={selectedProvider}
          onSelectProvider={handleProviderSelect}
        />
      )}

      {currentStep === 3 && selectedService && selectedProvider && (
        <>
          <Step3TimeSlot
            serviceId={selectedService}
            providerId={selectedProvider}
            selectedSlot={selectedSlot}
            selectedTimezone={selectedTimezone}
            onSelectSlot={handleSlotSelect}
            onTimezoneChange={setSelectedTimezone}
          />
          <div style={navigationStyles}>
            <Button onClick={handleBack} variant="outline">
              Back
            </Button>
            <Button onClick={handleNext} variant="primary" disabled={!selectedSlot}>
              Continue to Details
            </Button>
          </div>
        </>
      )}

      {currentStep === 4 && (
        <>
          <Step4DetailsForm
            contactDetails={contactDetails}
            onContactDetailsChange={setContactDetails}
            onSubmit={handleSubmit}
            serviceId={selectedService || 'demo'}
          />
          <div style={navigationStyles}>
            <Button onClick={handleBack} variant="outline" disabled={isSubmitting}>
              Back
            </Button>
          </div>
        </>
      )}

      {currentStep === 5 && booking && <Step5Confirmation booking={booking} />}

      {/* Loading Modal */}
      <Modal
        isOpen={isSubmitting}
        onClose={() => {}}
        title="Creating Your Booking"
      >
        <Loader text="Please wait..." />
      </Modal>

      {/* Error Modal */}
      <Modal
        isOpen={!!error}
        onClose={() => setError(null)}
        title="Booking Error"
        actions={
          <Button onClick={() => setError(null)} variant="primary">
            Try Again
          </Button>
        }
      >
        <p>{error}</p>
      </Modal>
      {currentStep < 3 && currentStep < 5 && (
        <div style={earlyStepNavStyles}>
          {currentStep > 1 && (
            <Button onClick={handleBack} variant="outline">
              Back
            </Button>
          )}
          <Button onClick={handleNext} variant="primary" disabled={!canProceed}>
            Continue
          </Button>
        </div>
      )}
    </div>
  );
}
